import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../../utils/api';
import { FiBook, FiMessageCircle, FiDatabase, FiSend, FiCheckCircle, FiHelpCircle, FiBarChart2, FiUser, FiTrendingUp, FiClock, FiSearch, FiChevronDown, FiChevronRight } from 'react-icons/fi';

const ICON_MAP = {
  book: <FiBook />, message: <FiMessageCircle />, database: <FiDatabase />,
  check: <FiCheckCircle />, send: <FiSend />, help: <FiHelpCircle />,
  chart: <FiBarChart2 />, user: <FiUser />, trending: <FiTrendingUp />, clock: <FiClock />,
};

const AccordionItem = ({ item, color, isOpen, onToggle }) => (
  <div style={{
    borderRadius: 10, border: '1px solid var(--border-color)', overflow: 'hidden',
    transition: 'all 0.3s cubic-bezier(0.22, 1, 0.36, 1)',
    boxShadow: isOpen ? '0 4px 16px rgba(126,165,46,0.08)' : 'none',
    transform: isOpen ? 'scale(1.01)' : 'scale(1)',
  }}>
    <button onClick={onToggle} style={{
      width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '14px 16px', background: isOpen ? `${color}08` : 'var(--bg-secondary)',
      border: 'none', cursor: 'pointer', textAlign: 'left', transition: 'background 0.2s'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <FiHelpCircle size={14} style={{ color, flexShrink: 0, transition: 'transform 0.3s', transform: isOpen ? 'rotate(90deg)' : 'none' }} />
        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>{item.q}</span>
      </div>
      {isOpen ? <FiChevronDown size={14} color="#94a3b8" style={{ transition: 'transform 0.3s' }} /> : <FiChevronRight size={14} color="#94a3b8" style={{ transition: 'transform 0.3s' }} />}
    </button>
    {isOpen && (
      <div style={{ padding: '12px 16px 16px 38px', background: 'var(--bg-secondary)', borderTop: `1px solid ${color}15`, animation: 'slideInUp 0.3s ease both' }}>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.7 }}>{item.a}</div>
      </div>
    )}
  </div>
);

export default function UserHelp() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [openItems, setOpenItems] = useState({});

  useEffect(() => {
    api.get('/user/help-content')
      .then(res => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  const toggleItem = (sectionIdx, itemIdx) => {
    const key = `${sectionIdx}-${itemIdx}`;
    setOpenItems(prev => ({ ...prev, [key]: !prev[key] }));
  };

  if (loading) return (
    <div className="loading-spinner">
      <div className="spinner" />
      <div className="loading-text">Loading...</div>
      <div className="loading-dots"><span /><span /><span /></div>
    </div>
  );

  const sections = data?.sections || [];
  const qs = data?.quickStats || {};

  // Filter sections based on search
  const filteredSections = searchQuery
    ? sections.map(section => ({
        ...section,
        items: (section.items || []).filter(item =>
          item.q.toLowerCase().includes(searchQuery.toLowerCase()) ||
          item.a.toLowerCase().includes(searchQuery.toLowerCase())
        )
      })).filter(section => section.items.length > 0)
    : sections;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Help Center</h1>
          <p className="page-subtitle">Personalized guide based on your campaign data</p>
        </div>
      </div>

      {/* Search Bar */}
      <div className="card" style={{ marginBottom: 20, padding: '12px 20px' }}>
        <div style={{ position: 'relative' }}>
          <FiSearch size={16} style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
          <input type="text" className="form-input" placeholder="Search help topics..."
            value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
            style={{ paddingLeft: 40, width: '100%', fontSize: 14 }} />
        </div>
      </div>

      {/* Quick Stats Bar */}
      {qs.totalCampaigns !== undefined && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 24 }}>
          {[
            { label: 'Campaigns', value: qs.totalCampaigns, color: '#7ea52e' },
            { label: 'Sent', value: qs.totalSent?.toLocaleString(), color: '#7ea52e' },
            { label: 'Failed', value: qs.totalFailed?.toLocaleString(), color: '#1a1a18' },
            { label: 'Pending', value: qs.totalPending?.toLocaleString(), color: '#9ec25a' },
            { label: 'Success Rate', value: `${qs.successRate || 0}%`, color: '#1a1a18' },
          ].map((s, i) => (
            <div key={i} className="card" style={{ textAlign: 'center', padding: '16px 8px', borderTop: `3px solid ${s.color}`, animationDelay: `${0.05 + i * 0.06}s` }}>
              <div style={{ fontSize: 20, fontWeight: 800, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#abab9f', textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 2 }}>{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Quick Actions */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 14, marginBottom: 24 }}>
        {[
          { label: 'My Broadcasts', icon: <FiSend />, path: '/user/campaigns', color: '#7ea52e' },
          { label: 'Analytics', icon: <FiBarChart2 />, path: '/user/analytics', color: '#1a1a18' },
          { label: 'Performance', icon: <FiTrendingUp />, path: '/user/performance', color: '#5c7a1f' },
          { label: 'Activity Log', icon: <FiClock />, path: '/user/activity', color: '#9ec25a' },
          { label: 'My Account', icon: <FiUser />, path: '/user/profile', color: '#7e7e71' },
        ].map((action, i) => (
          <Link key={i} to={action.path} style={{ textDecoration: 'none' }}>
            <div className="card" style={{
              textAlign: 'center', padding: '18px 12px', cursor: 'pointer',
              transition: 'all 0.3s cubic-bezier(0.22, 1, 0.36, 1)',
              borderTop: `3px solid ${action.color}`,
              animationDelay: `${0.1 + i * 0.06}s`,
            }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px) scale(1.02)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(126,165,46,0.12)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}>
              <div style={{ fontSize: 22, color: action.color, marginBottom: 6, transition: 'transform 0.3s' }}>{action.icon}</div>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)' }}>{action.label}</div>
            </div>
          </Link>
        ))}
      </div>

      {/* Dynamic FAQ Sections - Accordion */}
      {filteredSections.length > 0 ? filteredSections.map((section, si) => (
        <div key={si} className="card" style={{ marginBottom: 16 }}>
          <h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <span style={{ color: section.color }}>{ICON_MAP[section.icon] || <FiHelpCircle />}</span> {section.title}
            <span style={{ fontSize: 11, color: '#94a3b8', fontWeight: 500, marginLeft: 'auto' }}>
              {section.items?.length} question{section.items?.length !== 1 ? 's' : ''}
            </span>
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {(section.items || []).map((item, i) => (
              <AccordionItem key={i} item={item} color={section.color}
                isOpen={!!openItems[`${si}-${i}`]}
                onToggle={() => toggleItem(si, i)} />
            ))}
          </div>
        </div>
      )) : (
        <div className="card" style={{ textAlign: 'center', padding: 40 }}>
          <FiSearch size={32} style={{ color: '#94a3b8', marginBottom: 12 }} />
          <h3 style={{ color: 'var(--text-primary)' }}>
            {searchQuery ? 'No results found' : 'No help content available'}
          </h3>
          <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>
            {searchQuery ? 'Try different keywords' : 'Start by completing some campaigns to see personalized tips.'}
          </p>
        </div>
      )}

      {/* Footer */}
      <div className="card" style={{ textAlign: 'center', padding: 24 }}>
        <FiHelpCircle size={32} style={{ color: '#7ea52e', marginBottom: 12 }} />
        <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 6 }}>Need More Help?</h3>
        <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>Contact your administrator for additional support or feature requests.</p>
      </div>
    </div>
  );
}
